<script setup>
import { useToggle } from '@vueuse/core';
import { f7 } from 'framework7-vue';
import { useRepo } from 'pinia-orm';
import { computed, defineAsyncComponent, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useCart } from '@/composables/useCart.js';
import { useCurrency } from '@/composables/useCurrency.js';
import { OrderBoxItemStatus } from '@/enums/commerce/OrderBoxItemStatus.enum.js';
import OrderBoxItem from '@/models/commerce/OrderBoxItem.js';
import Product from '@/models/commerce/Product.js';
import { useAuthStore } from '@/stores/authStore.js';

const props = defineProps({
  product: { type: Object, required: true },
  productId: { type: String, required: true },
  postMedia: { type: Array, default: () => [] },
  postCartItems: { type: Number, default: 0 },
  showBuyerActions: { type: Boolean, default: true },
  // «تدبيسة» resale facts (BE→FE brief 2026-09-17-tadbeesa-channel-posts). Read
  // through the same v-once prop path `postCartItems` already uses above — see
  // PostCard.vue for why that staleness window is accepted here.
  isResale: { type: Boolean, default: false },
  resaleAvailableQty: { type: Number, default: 0 },
});

const emit = defineEmits(['click', 'create-box-required']);

// PERFORMANCE OPTIMIZATION: Lazy load CartPage component
// Only loaded when first time popup is opened (popupOpened becomes true)
const CartPage = defineAsyncComponent({
  loader: () => import('@/pages/CartPage.vue'),
  // Optional: Show loading state while component loads
  loadingComponent: {
    template: `
      <f7-page>
        <f7-navbar :title="$t('product.addToBox')">
          <f7-nav-right>
            <f7-link popup-close>
              <f7-icon f7="xmark"></f7-icon>
            </f7-link>
          </f7-nav-right>
        </f7-navbar>
        <f7-page-content>
          <f7-block class="text-align-center" style="padding-top: 100px;">
            <f7-preloader></f7-preloader>
          </f7-block>
        </f7-page-content>
      </f7-page>
    `,
  },
  delay: 200,
  errorComponent: {
    template: `
      <f7-page>
        <f7-navbar :title="$t('common.error')">
          <f7-nav-right>
            <f7-link popup-close>
              <f7-icon f7="xmark"></f7-icon>
            </f7-link>
          </f7-nav-right>
        </f7-navbar>
        <f7-page-content>
          <f7-block class="text-align-center" style="padding-top: 100px;">
            <f7-icon f7="exclamationmark_triangle" size="48px" color="red"></f7-icon>
            <p>{{ $t('common.loadError') }}</p>
          </f7-block>
        </f7-page-content>
      </f7-page>
    `,
  },
  timeout: 10000,
});

// Lazy load the create-box popup — only fetched the first time a boxless
// user is guided into creating their first box (Option B onboarding flow).
const AddBoxPopup = defineAsyncComponent(() => import('@/components/orders/AddBoxPopup.vue'));

const { t } = useI18n();
const { formatCurrency } = useCurrency();
const authStore = useAuthStore();

const {
  selectedBoxId,
  availableBoxes,
  fetchPostItems,
  resetCart,
  createBox,
  createBoxError,
  isCreatingBox,
} = useCart();

// =============================================================================
// LOCAL STATE
// =============================================================================
const [popupOpened, togglePopup] = useToggle(false);
const activeMediaIndex = ref(0);
const postItemsFetched = ref(false);

// In-place "create your first box" flow (Option B). When a permitted user with
// zero active boxes taps add-to-box, we open AddBoxPopup right here; on success
// we continue straight into the cart for THIS product (intent preserved).
const addBoxOpened = ref(false);
const addBoxEverOpened = ref(false);
const continueToCartAfterBox = ref(false);

// CRITICAL: Track if CartPage has been loaded at least once
// This prevents unnecessary re-imports after first load
const cartPageEverOpened = ref(false);

const variantSummaries = ref({});

// =============================================================================
// COMPUTED
// =============================================================================
const productWithVariants = computed(() =>
  useRepo(Product).with('variants').find(props.product.id),
);

const lowestVariantPrice = computed(() => {
  if (!productWithVariants.value?.variants?.length) return props.product.basePrice;

  const prices = productWithVariants.value.variants.map(v => v.price);
  return Math.min(...prices);
});

const formattedLowestPrice = computed(() => formatCurrency(lowestVariantPrice.value));

const hasItems = computed(() => props.postCartItems > 0);

// A تدبيسة listing sold down to zero — the feed itself never shows this card
// (a sold-out listing drops out of it server-side), but the post's OWN page
// and a bookmarked read still answer at zero on purpose, so the button here
// has to refuse rather than assume it is never reached at qty 0.
const isResaleSoldOut = computed(() => props.isResale && props.resaleAvailableQty <= 0);

// =============================================================================
// METHODS - Data Loading
// =============================================================================
const loadVariantItems = (variantId) => {
  if (!selectedBoxId.value) return [];

  const boxIdInt = parseInt(selectedBoxId.value);
  const productIdInt = parseInt(props.productId);
  const variantIdInt = parseInt(variantId);

  const storedItems = useRepo(OrderBoxItem)
    .where('orderBoxId', boxIdInt)
    .where('variantId', variantIdInt)
    .where('productId', productIdInt)
    .get();

  return storedItems.map((item) => ({
    tempId: `existing-${item.id}`,
    existingId: item.id,
    qty: item.qty || 0,
    notes: item.notes || '',
    avatarUrl: item.avatarUrl || '',
    reservationStatus: item.reservationStatus || 'none',
    depositAmount: item.depositAmount || 0,
    reservedForName: item.reservedForName || '',
    status: item.status || OrderBoxItemStatus.PENDING,
  }));
};

const calculateVariantSummary = (variantId, variantPrice) => {
  const items = loadVariantItems(variantId);

  const activeItems = items.filter(item => item.status !== OrderBoxItemStatus.CANCELLED);

  const totalQty = activeItems.reduce((sum, item) => sum + (item.qty || 0), 0);
  const totalAmount = activeItems.reduce((sum, item) => sum + ((item.qty || 0) * variantPrice), 0);
  const totalDeposit = activeItems.reduce((sum, item) => sum + (item.depositAmount || 0), 0);

  const previewImages = activeItems
    .filter(item => item.avatarUrl)
    .slice(0, 4)
    .map(item => ({ avatarUrl: item.avatarUrl }));

  return {
    totalQty,
    itemsCount: activeItems.length,
    totalAmount,
    totalDeposit,
    previewImages,
  };
};

const refreshVariantSummaries = () => {
  if (!selectedBoxId.value || !productWithVariants.value?.variants?.length) {
    variantSummaries.value = {};
    return;
  }

  const summaries = {};
  productWithVariants.value.variants.forEach((variant) => {
    summaries[variant.id] = calculateVariantSummary(variant.id, variant.price);
  });
  variantSummaries.value = summaries;
};

// =============================================================================
// METHODS - UI Actions
// =============================================================================
const showBlockedToast = () => {
  // canOrder === false covers pending, awaiting-deposit, blocked and rejected.
  // Pending members get the "wait for approval" wording; everyone else the
  // generic "activate your membership to create boxes" line.
  const text = authStore.isMembershipPending
    ? t('product.pendingMembershipCart')
    : t('box.errors.cannotCreateNoActiveMembership');
  f7.toast.create({
    text,
    position: 'center',
    closeTimeout: 3000,
  }).open();
};

const handleAddToCartClick = async () => {
  // A sold-out تدبيسة listing — stop here rather than open a cart popup whose
  // save can only ever 422. The badge above already reads «نفدت»; this is the
  // same fact stated as a refusal.
  if (isResaleSoldOut.value) {
    f7.toast.create({
      text: t('product.resale.soldOutToast'),
      position: 'center',
      closeTimeout: 2500,
    }).open();
    return;
  }

  // Permission gate — only members allowed to create/fill order boxes proceed.
  // Approved-but-inactive (awaiting deposit), pending, blocked and rejected all
  // read canOrder === false and are stopped here instead of reaching the form.
  if (!authStore.canOrder) {
    showBlockedToast();
    return;
  }

  // No active/draft box yet — guide the (permitted) user into creating their
  // first box in place, then continue straight into the cart for this product.
  if (availableBoxes.value.length === 0) {
    addBoxEverOpened.value = true;
    addBoxOpened.value = true;
    return;
  }

  await openCartPopup();
};

const openCartPopup = async () => {
  // Mark that cart has been opened at least once
  // This triggers v-if to render CartPage component
  cartPageEverOpened.value = true;
  togglePopup(true);

  if (!postItemsFetched.value) {
    f7.preloader.show();
    await fetchPostItems(0, parseInt(props.product.postId));
    postItemsFetched.value = true;
    f7.preloader.hide();
  }
};

// Create-box handler for the in-place onboarding flow. Mirrors the orchestration
// in OrdersPage.handleCreateBox (single source of truth: useCart.createBox).
const handleCreateBoxFromProduct = async (boxData) => {
  try {
    const newBox = await createBox(0, boxData);

    // Gated (e.g. account suspended) — the guard already toasted; just close.
    if (!newBox && !createBoxError.value) {
      addBoxOpened.value = false;
      return;
    }

    if (createBoxError.value) {
      // Membership not active — should be caught by the gate above, but the
      // backend is the source of truth, so handle its rejection defensively.
      if (createBoxError.value.code === 'auth.no_active_membership') {
        addBoxOpened.value = false;
        showBlockedToast();
        return;
      }
      // Other errors: keep the form open so the user can correct and retry.
      f7.toast.create({
        text: createBoxError.value.message || t('box.feedback.errorCreating'),
        position: 'center',
        closeTimeout: 3000,
      }).open();
      return;
    }

    // Success — flag the continue-to-cart intent, then close the box popup. The
    // watcher opens the cart once the popup has finished animating out (avoids
    // stacking two F7 popups). The flag is set ONLY here, so a user who closes
    // the form without creating a box is never carried into the cart.
    continueToCartAfterBox.value = true;
    addBoxOpened.value = false;
  } catch (error) {
    console.error('Error creating box from product card:', error);
    if (error?.code === 'auth.no_active_membership') {
      addBoxOpened.value = false;
      showBlockedToast();
      return;
    }
    f7.toast.create({
      text: t('box.feedback.errorCreating'),
      position: 'center',
      closeTimeout: 3000,
    }).open();
  }
};

const handleBoxSelection = (boxId) => {
  selectedBoxId.value = boxId;
  refreshVariantSummaries();
};

const handlePopupClosed = () => {
  activeMediaIndex.value = 0;
  variantSummaries.value = {};
  resetCart();
  postItemsFetched.value = false;

  console.log('Cart popup closed and cleaned up');
};

// =============================================================================
// WATCHERS
// =============================================================================
watch(popupOpened, (isOpen) => {
  if (!isOpen) {
    activeMediaIndex.value = 0;

    setTimeout(() => {
      if (!popupOpened.value) {
        variantSummaries.value = {};
        resetCart();
        postItemsFetched.value = false;
      }
    }, 300);
  }
});

watch(
  () => availableBoxes.value,
  (boxes) => {
    if (boxes.length === 1 && !selectedBoxId.value) {
      handleBoxSelection(boxes[0].id);
    }
  },
  { immediate: true },
);

// After a box is created in place, continue into the cart for this product once
// the AddBoxPopup has finished closing (sequential popups, never stacked).
watch(addBoxOpened, (isOpen) => {
  if (!isOpen && continueToCartAfterBox.value) {
    continueToCartAfterBox.value = false;
    setTimeout(() => {
      openCartPopup();
    }, 400);
  }
});

watch(selectedBoxId, () => {
  if (selectedBoxId.value) {
    refreshVariantSummaries();
  }
}, { immediate: true });
</script>

<template>
  <!-- Product Card - Shows "From" price instead of base price -->
  <f7-card outline class="product-card margin" @click="emit('click')">
    <f7-card-content :padding="false">
      <div class="padding display-flex justify-content-space-between align-items-center">
        <div class="flex-shrink-0">
          <div class="text-align-right">
            <div class="text-color-gray" style="font-size: 12px">{{ t('product.from') }}</div>
            <span class="text-color-primary" style="font-size: 24px; font-weight: 700">
              {{ formattedLowestPrice }}
            </span>
          </div>
        </div>

        <f7-button
          v-if="showBuyerActions"
          fill
          round
          :disabled="isResaleSoldOut"
          @click.stop="handleAddToCartClick"
        >
          <f7-icon
            f7="cart"
            class="margin-right-half"
          >
            <f7-badge v-if="hasItems" color="red">{{ postCartItems }}</f7-badge>
          </f7-icon>
          <span>{{ isResaleSoldOut ? t('product.resale.soldOut') : t('product.addToBox') }}</span>
        </f7-button>
      </div>
    </f7-card-content>
  </f7-card>

  <!--
      Cart Popup - Full Width on Mobile, Responsive on Tablets
      Framework7 Popup is automatically:
      - Fullscreen on phones (perfect for mobile-first)
      - 630px × 630px centered modal on tablets
      - Can be made fullscreen on tablets with "popup-tablet-fullscreen" class
    -->
  <f7-popup
    v-if="showBuyerActions"
    v-model:opened="popupOpened"
    class="cart-popup"
    :swipe-to-close="false"
    :push="false"
    @popup:closed="handlePopupClosed"
  >
    <!--
          PERFORMANCE OPTIMIZATION:
          - v-if ensures CartPage is only mounted when cartPageEverOpened is true
          - cartPageEverOpened is set to true only when user clicks "Add to Cart"
          - defineAsyncComponent ensures CartPage.vue bundle is only downloaded when needed
          - Once loaded and mounted, component stays in memory (no re-import needed)
          - Popup can be opened/closed multiple times without re-mounting CartPage
        -->
    <cart-page
      v-if="cartPageEverOpened"
      :post-id="product.postId"
      :product-id="product.id"
      @popup-closed="handlePopupClosed"
    ></cart-page>
  </f7-popup>

  <!--
      In-place "create your first box" popup (Option B onboarding).
      Only mounted once a boxless, permitted user is guided into creating a box.
      On success the box-create watcher continues straight into the cart above.
    -->
  <add-box-popup
    v-if="showBuyerActions && addBoxEverOpened"
    v-model:opened="addBoxOpened"
    :next-box-number="availableBoxes.length + 1"
    :is-creating="isCreatingBox"
    @create="handleCreateBoxFromProduct"
  ></add-box-popup>
</template>

<style scoped>
/* Product Card */
.product-card {
    cursor: pointer;
    transition: all 0.2s ease;
}

.product-card:hover {
    transform: translateY(-2px);
}

.product-card:active {
    transform: scale(0.98);
}

/*
  Cart Popup Customization
  Framework7 Popup is fullscreen on mobile by default
  You can add custom styling if needed
*/
.cart-popup {
    /* Optional: Custom styling for the popup */
}

/*
  If you want the popup to be fullscreen on tablets too,
  add the popup-tablet-fullscreen class to the f7-popup element:
  <f7-popup class="cart-popup popup-tablet-fullscreen">
*/
</style>
